/* ============================================================
   ROOTS TO ART — Configurator v2 (Shopify)
   ============================================================
   This file is loaded as a theme asset and initialises against
   the .rta-config-v2 root element emitted by configurator-v2.liquid.

   Expected global config (set by Liquid section):
   window.RTA_CONFIG_V2 = {
     styles: [ { id, name, tier, img, blurb, nameColor?, overlay?, markColor? }, ... ],
     monthFlowers: { January: 'Carnation', ... },
     monthFlowersLatin: { January: 'Dianthus caryophyllus', ... }, // optional
     currentStyleId: 'morning-garden',     // pre-selection from product metafield
     productMap: {                          // styleId → Shopify product/variant data
       'morning-garden': {
         productId: 1234567890,
         handle: 'morning-garden-birth-flower-print', // used for URL rewrite on style switch
         variants: {                        // keyed by "size|frame" composite
           'A3|Unframed': { id: 9999, price: 6900 },
           'A3|White':    { id: 1000, price: 9900 },
           ...
         }
       },
       ...
     },
     theatreUrl: 'https://roots-to-art-pipeline-production.up.railway.app/api/generate-preview',
     cartPropertySchema: {                  // must match live configurator-wizard.js names
       familyName:         'familyName',
       style:              'style',         // slug
       size:               'size',
       members:            'members',       // JSON-stringified array
       _memberCount:       '_memberCount',
       _generatedImageUrl: '_generatedImageUrl',
       frame:              'Frame',         // client-side only (no Prodigi hook yet)
       paper:              'Paper',         // client-side only (no Prodigi hook yet)
       _styleName:         '_styleName'     // display label for cart/ops
     },
     currency: '€',
     cartReturnUrl: '/checkout'             // skip /cart, match live behaviour
   };
   ============================================================ */

(function () {
  'use strict';

  const root = document.querySelector('.rta-config-v2');
  if (!root) return;
  if (root.dataset.rtaInit === '1') return;
  root.dataset.rtaInit = '1';

  const CFG = window.RTA_CONFIG_V2 || {};
  const STYLES = CFG.styles || [];
  const MONTH_FLOWERS = CFG.monthFlowers || {};
  const TIER_ORDER = ['Traditional', 'Modern'];
  const STEP_NAMES = ['Style', 'Family', 'Name', 'Review'];
  const MAX_MEMBERS = 8;
  const CURRENCY = CFG.currency || '€';

  // Month → numeric + latin flower name (for Theatre of Creation payload).
  // Railway expects birthMonth (int), birthMonthName (string), flower, flowerLatin.
  const MONTH_NUMBERS = {
    January: 1, February: 2, March: 3, April: 4, May: 5, June: 6,
    July: 7, August: 8, September: 9, October: 10, November: 11, December: 12
  };
  const MONTH_FLOWERS_LATIN = CFG.monthFlowersLatin || {
    January: 'Dianthus caryophyllus',
    February: 'Viola odorata',
    March: 'Narcissus pseudonarcissus',
    April: 'Bellis perennis',
    May: 'Convallaria majalis',
    June: 'Rosa',
    July: 'Delphinium',
    August: 'Gladiolus',
    September: 'Aster',
    October: 'Calendula officinalis',
    November: 'Chrysanthemum',
    December: 'Narcissus tazetta'
  };

  // Scoped query helpers — never leak out of the container.
  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => root.querySelectorAll(sel);
  const byId = (id) => root.querySelector('#' + id);

  // ============================================================
  // STATE
  // ============================================================

  const state = {
    step: 1,
    style: CFG.currentStyleId || null,
    members: [],
    familyName: '',
    size: 'A3',
    paper: 'Enhanced Matte',
    frame: 'Black',   // ready-to-hang default; pricing/availability now come from the variant map
    sizePrice: 0,
    paperPrice: 0,
    framePrice: 0
  };

  // ============================================================
  // STEPPER
  // ============================================================

  function renderStepper() {
    const el = byId('stepper');
    if (!el) return;
    el.innerHTML = '';
    STEP_NAMES.forEach((name, i) => {
      const n = i + 1;
      const pill = document.createElement('div');
      pill.className = 'step-pill';
      if (state.step === n) pill.classList.add('is-active');
      if (state.step > n) pill.classList.add('is-done');
      pill.innerHTML = '<span class="step-pill__num">' + (state.step > n ? '✓' : n) + '</span><span>' + name + '</span>';
      el.appendChild(pill);
      if (i < STEP_NAMES.length - 1) {
        const div = document.createElement('div');
        div.className = 'step-divider';
        el.appendChild(div);
      }
    });
  }

  // ============================================================
  // STEP 1 — STYLE GRID
  // ============================================================

  function renderStyleGrid() {
    const el = byId('styleGrid');
    if (!el) return;
    el.innerHTML = '';
    TIER_ORDER.forEach(tier => {
      const inTier = STYLES.filter(s => s.tier === tier);
      if (!inTier.length) return;

      const header = document.createElement('div');
      header.className = 'style-group-header';
      header.innerHTML =
        '<span class="style-group-header__label">' + tier + '</span>' +
        '<span class="style-group-header__rule"></span>' +
        '<span class="style-group-header__count">' + inTier.length + ' styles</span>';
      el.appendChild(header);

      inTier.forEach(s => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'style-card' + (state.style === s.id ? ' is-selected' : '');
        card.setAttribute('data-style-id', s.id);
        card.innerHTML =
          '<div class="style-card__image">' +
            '<span class="style-card__tier">' + s.tier + '</span>' +
            '<span class="style-card__check">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M5 12.5l5 5 9-10"/></svg>' +
            '</span>' +
            '<img src="' + s.img + '" alt="' + escapeAttr(s.name) + '" loading="lazy">' +
          '</div>' +
          '<div class="style-card__meta">' +
            '<span class="style-card__label">Art Print</span>' +
            '<h3 class="style-card__title">' + escapeHtml(s.name) + '</h3>' +
          '</div>';
        card.addEventListener('click', () => selectStyle(s.id));
        el.appendChild(card);
      });
    });
  }

  function selectStyle(id) {
    console.log('[RTA V2 build:2026-04-22-b] selectStyle fired — currentStep=', state.step, 'id=', id);
    state.style = id;
    $$('.style-card').forEach(c => {
      c.classList.toggle('is-selected', c.getAttribute('data-style-id') === id);
    });
    // Update URL to match the selected style — preserves all wizard state (no reload).
    // Customer can refresh or share and land on the canonical product page for their choice.
    try {
      const mapEntry = (CFG.productMap || {})[id];
      if (mapEntry && mapEntry.handle && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '/products/' + mapEntry.handle);
      }
    } catch (e) { /* non-critical — ignore */ }
    updatePreview();
    updateNav();
    // Auto-advance from the style step to the next one after a short delay so
    // the tick animation registers. Matches prototype behaviour — users don't
    // need to find a Continue button. Only fires from step 1 (Style) to avoid
    // skipping forward if the user is editing style from the Review step.
    if (state.step === 1) {
      setTimeout(() => { if (state.step === 1) goToStep(2); }, 280);
    }
  }

  // ============================================================
  // STEP 2 — MEMBERS
  // ============================================================

  function renderMembers() {
    const list = byId('membersList');
    if (!list) return;
    list.innerHTML = '';
    if (state.members.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'members-empty';
      empty.textContent = 'Start with one person and build from there.';
      list.appendChild(empty);
    }
    state.members.forEach((m, i) => {
      const row = document.createElement('div');
      row.className = 'member-row';
      row.innerHTML =
        '<span class="member-row__num">' + (i + 1) + '</span>' +
        '<div class="member-row__fields">' +
          '<div class="field">' +
            '<input type="text" placeholder="Name" value="' + escapeAttr(m.name) + '" data-field="name" data-idx="' + i + '" maxlength="30">' +
          '</div>' +
          '<div class="field">' +
            '<select data-field="month" data-idx="' + i + '">' +
              '<option value="">Birth month</option>' +
              Object.keys(MONTH_FLOWERS).map(mo =>
                '<option value="' + mo + '"' + (m.month === mo ? ' selected' : '') + '>' + mo + '</option>'
              ).join('') +
            '</select>' +
            '<div class="field__hint ' + (m.month ? 'is-visible' : '') + '">' + (m.month ? escapeHtml(MONTH_FLOWERS[m.month]) : '') + '</div>' +
          '</div>' +
        '</div>' +
        '<button class="member-row__remove" data-idx="' + i + '" aria-label="Remove person">' +
          '<svg viewBox="0 0 12 12" fill="currentColor"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5"/></svg>' +
        '</button>';
      list.appendChild(row);
    });

    list.querySelectorAll('input[data-field="name"]').forEach(el =>
      el.addEventListener('input', e => {
        state.members[+e.target.dataset.idx].name = e.target.value;
        updatePreview();
        updateNav();
      })
    );
    list.querySelectorAll('input[data-field="name"]').forEach(el =>
      el.addEventListener('blur', e => {
        const tc = titleCaseName(e.target.value);
        if (tc !== e.target.value) e.target.value = tc;
        state.members[+e.target.dataset.idx].name = tc;
        updatePreview();
      })
    );
    list.querySelectorAll('select[data-field="month"]').forEach(el =>
      el.addEventListener('change', e => {
        state.members[+e.target.dataset.idx].month = e.target.value;
        renderMembers();
        updatePreview();
        updateNav();
      })
    );
    list.querySelectorAll('.member-row__remove').forEach(el =>
      el.addEventListener('click', e => {
        state.members.splice(+e.currentTarget.dataset.idx, 1);
        renderMembers();
        updatePreview();
        updateNav();
      })
    );

    const addBtn = byId('addMemberBtn');
    if (addBtn) addBtn.disabled = state.members.length >= MAX_MEMBERS;
    const maxNote = byId('maxNote');
    if (maxNote) maxNote.style.display = state.members.length >= MAX_MEMBERS ? 'block' : 'none';
  }

  // ============================================================
  // PREVIEW RENDERING
  // ============================================================

  function applyStyleChrome(artworkEl, containerEl) {
    const style = STYLES.find(s => s.id === state.style);
    if (!artworkEl || !containerEl) return;

    if (style && style.nameColor) {
      containerEl.style.setProperty('--name-color', style.nameColor);
    } else {
      containerEl.style.removeProperty('--name-color');
    }

    if (style && style.overlay === 'dark') {
      artworkEl.setAttribute('data-overlay', 'dark');
    } else {
      artworkEl.removeAttribute('data-overlay');
    }

    if (style && style.markColor) {
      artworkEl.style.setProperty('--mark-color', style.markColor);
    } else {
      artworkEl.style.removeProperty('--mark-color');
    }
  }

  function styleCatalogNo(style) {
    if (!style || !style.img) return '00';
    const m = style.img.match(/\/(\d{2})[-_]/);
    return m ? m[1] : '00';
  }

  function updateSignatureMarks() {
    const style = STYLES.find(s => s.id === state.style);
    const num = style ? styleCatalogNo(style) : '00';
    const hasMembers = state.members.some(m => m.name && m.month);
    ['preview2Mark', 'preview3Mark', 'reviewMark'].forEach(id => {
      const el = byId(id);
      if (!el) return;
      const numEl = el.querySelector('.mark-num');
      if (numEl) numEl.textContent = num;
      if (id === 'preview2Mark') {
        el.style.display = (style && hasMembers) ? '' : 'none';
      } else {
        el.style.display = style ? '' : 'none';
      }
    });
  }

  function applyFrame() {
    const frame = state.frame || 'Unframed';
    ['preview2Frame', 'preview3Frame', 'reviewFrame'].forEach(id => {
      const el = byId(id);
      if (el) el.setAttribute('data-frame', frame);
    });
  }

  function updatePreview() {
    const style = STYLES.find(s => s.id === state.style);

    // Step 2
    const p2bg = byId('preview2Bg');
    const p2Names = byId('preview2Names');
    const p2Ph = byId('preview2Placeholder');
    const p2Style = byId('preview2Style');

    if (style && p2bg) {
      p2bg.src = style.img;
      p2bg.classList.add('is-loaded');
      if (p2Style) p2Style.textContent = ' ' + style.name;
    }

    const hasMembers = state.members.some(m => m.name && m.month);
    if (hasMembers) {
      if (p2Ph) p2Ph.style.display = 'none';
      if (p2Names) p2Names.style.display = 'block';
      const p2Family = byId('preview2Family');
      const p2Members = byId('preview2Members');
      if (p2Family) p2Family.textContent = state.familyName || 'Your Family';
      if (p2Members) p2Members.textContent = membersLineShort();
    } else {
      if (p2Ph) p2Ph.style.display = 'flex';
      if (p2Names) p2Names.style.display = 'none';
    }
    applyStyleChrome(byId('preview2Art'), byId('preview2Frame'));

    // Step 3
    const p3bg = byId('preview3Bg');
    if (style && p3bg) {
      p3bg.src = style.img;
      p3bg.classList.add('is-loaded');
    }
    const p3Family = byId('preview3Family');
    const p3Members = byId('preview3Members');
    if (p3Family) p3Family.textContent = state.familyName || 'Your Family';
    if (p3Members) p3Members.textContent = membersLineShort();
    applyStyleChrome(byId('preview3Art'), byId('preview3Frame'));

    // Step 4
    const r4bg = byId('reviewBg');
    if (style && r4bg) {
      r4bg.src = style.img;
      const r4Label = byId('reviewStyleLabel');
      if (r4Label) r4Label.textContent = 'in the ' + style.name + ' style';
    }
    const rFamily = byId('reviewFamily');
    const rMembers = byId('reviewMembers');
    if (rFamily) rFamily.textContent = state.familyName || 'Your Family';
    if (rMembers) rMembers.textContent = membersLineShort();
    applyStyleChrome(byId('reviewArtwork'), byId('reviewFrame'));

    applyFrame();
    updateSignatureMarks();
    persistFamily();
  }

  function membersLineShort() {
    const names = state.members.filter(m => m.name).map(m => m.name.trim()).filter(Boolean);
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(' & ');
    return names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];
  }

  function membersLineFull() {
    // Full format for cart line item property: "Adrian (May — Lily of the Valley), Sarah (June — Rose)"
    return state.members
      .filter(m => m.name && m.month)
      .map(m => m.name.trim() + ' (' + m.month + ' — ' + (MONTH_FLOWERS[m.month] || '') + ')')
      .join(', ');
  }

  // ============================================================
  // STEP 3 — NAME
  // ============================================================

  const familyInput = byId('familyName');
  if (familyInput) {
    familyInput.addEventListener('input', e => {
      state.familyName = e.target.value;
      updatePreview();
      updateNav();
    });
    familyInput.addEventListener('blur', e => {
      const tc = titleCaseName(e.target.value);
      if (tc !== e.target.value) e.target.value = tc;
      state.familyName = tc;
      updatePreview();
    });
  }

  $$('.name-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      if (familyInput) familyInput.value = chip.dataset.chip;
      state.familyName = chip.dataset.chip;
      updatePreview();
      updateNav();
    });
  });

  // ============================================================
  // STEP 4 — VARIANTS
  // ============================================================

  // Pricing + availability are driven entirely by the product map (size|frame → {id, price}).
  // The map is the source of truth — no client-side price arithmetic.
  function currentStyleVariants() {
    const sm = (CFG.productMap || {})[state.style];
    return (sm && sm.variants) || {};
  }
  function variantFor(size, frame) {
    return currentStyleVariants()[size + '|' + frame] || null;
  }
  function fmtPrice(cents) { return CURRENCY + Math.round((cents || 0) / 100); }
  function setText(id, t) { const el = byId(id); if (el) el.textContent = t; }
  function sizeHasAny(vmap, size) { return Object.keys(vmap).some(k => k.split('|')[0] === size); }

  // Refresh every variant button (price, selected, disabled), the total, and add-to-cart validity.
  function refreshPricing() {
    const vmap = currentStyleVariants();

    const sizeWrap = byId('sizeOptions');
    if (sizeWrap) sizeWrap.querySelectorAll('.variant-btn').forEach(btn => {
      const size = btn.dataset.value;
      let v = variantFor(size, state.frame);
      if (!v) { // no variant at current frame → show cheapest available at this size
        const avail = Object.keys(vmap).filter(k => k.split('|')[0] === size).map(k => vmap[k]);
        v = avail.sort((a, b) => a.price - b.price)[0] || null;
      }
      const priceEl = btn.querySelector('.variant-btn__price');
      if (priceEl) priceEl.textContent = v ? fmtPrice(v.price) : '—';
      btn.classList.toggle('is-selected', size === state.size);
      btn.disabled = !sizeHasAny(vmap, size);
    });

    const frameWrap = byId('frameOptions');
    if (frameWrap) frameWrap.querySelectorAll('.variant-btn').forEach(btn => {
      const frame = btn.dataset.value;
      const v = variantFor(state.size, frame);
      const priceEl = btn.querySelector('.variant-btn__price');
      if (priceEl) priceEl.textContent = v ? fmtPrice(v.price) : '—';
      btn.classList.toggle('is-selected', frame === state.frame);
      btn.disabled = !v;                          // e.g. Unframed disabled at A3/A1
      btn.classList.toggle('is-unavailable', !v);
    });

    setText('selectedSize', state.size);
    setText('summarySize', state.size);
    setText('selectedFrame', state.frame);
    setText('summaryFrame', state.frame);

    const cur = variantFor(state.size, state.frame);
    setText('summaryTotal', cur ? fmtPrice(cur.price) : '—');
    const cartPrice = byId('cartPrice');
    if (cartPrice) cartPrice.textContent = cur ? fmtPrice(cur.price) : '';
    const atc = byId('addToCart');
    if (atc) atc.disabled = !cur;
    applyFrame();
  }

  function bindVariantGroup(containerId, stateKey) {
    const container = byId(containerId);
    if (!container) return;
    container.querySelectorAll('.variant-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        state[stateKey] = btn.dataset.value;
        // If the size changed and the current frame isn't offered at that size
        // (A3/A1 have no Unframed), fall back to the first available frame.
        if (stateKey === 'size' && !variantFor(state.size, state.frame)) {
          const vmap = currentStyleVariants();
          const order = ['Black', 'Oak', 'White', 'Gold', 'Unframed'];
          state.frame = order.find(f => vmap[state.size + '|' + f]) || state.frame;
        }
        refreshPricing();
      });
    });
  }

  bindVariantGroup('sizeOptions', 'size');
  bindVariantGroup('frameOptions', 'frame');

  // Back-compat alias — older call sites referenced updateTotal().
  function updateTotal() { refreshPricing(); }

  // ============================================================
  // ADD TO CART — Shopify wiring
  // ============================================================

  const addToCartBtn = byId('addToCart');
  const originalAddLabel = addToCartBtn ? addToCartBtn.innerHTML : 'Generate';
  if (addToCartBtn) addToCartBtn.addEventListener('click', handleAddToCart);

  async function handleAddToCart() {
    const style = STYLES.find(s => s.id === state.style);
    if (!style) return;

    addToCartBtn.disabled = true;
    addToCartBtn.innerHTML = '<span>Composing your piece…</span>';
    const theatre = createTheatreOverlay(state.familyName, style.name);

    try {
      let compositeUrl = style.img;
      if (CFG.theatreUrl) {
        try { compositeUrl = await callTheatre(style); }
        catch (err) { console.error('[RTA] Theatre generation failed, using thumbnail fallback:', err); }
      }

      const styleMap = (CFG.productMap || {})[state.style];
      const vMap = (styleMap && styleMap.variants) || {};
      const variant = vMap[state.size + '|' + state.frame] || vMap[state.size] || vMap['default']
                   || (CFG.fallbackProduct && CFG.fallbackProduct.variantId
                        ? { id: CFG.fallbackProduct.variantId, price: CFG.fallbackProduct.priceCents } : null);
      if (!variant || !variant.id) {
        throw new Error('No variant for ' + state.style + ' at "' + state.size + '|' + state.frame + '".');
      }

      const schema = CFG.cartPropertySchema || {};
      const membersForCart = state.members.filter(m => m.name && m.month).map(m => ({
        name: m.name.trim(), birthMonth: MONTH_NUMBERS[m.month] || 0, birthMonthName: m.month,
        flower: MONTH_FLOWERS[m.month] || '', flowerLatin: MONTH_FLOWERS_LATIN[m.month] || ''
      }));
      const properties = {};
      properties[schema.familyName         || 'familyName']         = state.familyName;
      properties[schema.style              || 'style']              = state.style;
      properties[schema.size               || 'size']               = state.size;
      properties[schema.members            || 'members']            = JSON.stringify(membersForCart);
      properties[schema._memberCount       || '_memberCount']       = String(membersForCart.length);
      properties[schema._generatedImageUrl || '_generatedImageUrl'] = compositeUrl;
      properties[schema._styleName         || '_styleName']         = style.name;
      if (CFG.showFramePaper) {
        properties[schema.frame || 'Frame'] = state.frame;
        properties[schema.paper || 'Paper'] = state.paper;
      }
      const giftEl = byId('giftFor');
      if (giftEl && giftEl.value.trim()) properties['Gift for'] = giftEl.value.trim();

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1, properties: properties })
      });
      if (!res.ok) throw new Error('Cart add failed: ' + res.status + ' ' + (await res.text()));

      const img = byId('revealImage'); if (img) img.src = lowResUrl(compositeUrl);
      const rFrame = byId('revealFrame'); if (rFrame) rFrame.setAttribute('data-frame', state.frame);
      setText('revealFamily', state.familyName || 'your family');
      setText('revealStyle', style.name);
      setText('revealSize', state.size);
      setText('revealFrameName', state.frame);
      setText('revealPrice', fmtPrice(variant.price));
      goToStep(5);
      removeTheatreOverlay(theatre);
      refreshCartLine();

    } catch (err) {
      console.error('[RTA] Add to cart failed:', err);
      removeTheatreOverlay(theatre);
      addToCartBtn.disabled = false;
      addToCartBtn.innerHTML = originalAddLabel;
      alert('Something went wrong adding your print. Please try again, or email hello@rootstoart.com.');
    }
  }

  async function refreshCartLine() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      const n = cart.item_count;
      setText('revealCartLine', n + (n === 1 ? ' print' : ' prints') + ' in your order · ' + CURRENCY + Math.round(cart.total_price / 100));
    } catch (e) {}
  }

  (function wireReveal() {
    const addAnother = byId('revealAddAnother');
    if (addAnother) addAnother.addEventListener('click', function () {
      state.style = null; state.size = 'A3'; state.frame = 'Black';
      addToCartBtn.disabled = false; addToCartBtn.innerHTML = originalAddLabel;
      goToStep(1);
    });
    const checkout = byId('revealCheckout');
    if (checkout) checkout.addEventListener('click', function () {
      window.location.href = CFG.basketUrl || '/cart';
    });
  })();

  async function callTheatre(style) {
    // Payload matches the live Railway endpoint contract (roots-to-art-pipeline).
    // Response shape: { imageUrl: "..." } — do not change keys without coordinating with the pipeline.
    const payload = {
      version: 1,
      familyName: state.familyName,
      artStyle: state.style, // slug, e.g. 'watercolour', 'dutch-golden-age'
      memberCount: state.members.filter(m => m.name && m.month).length,
      members: state.members
        .filter(m => m.name && m.month)
        .map(m => ({
          name: m.name.trim(),
          birthMonth: MONTH_NUMBERS[m.month] || 0,
          birthMonthName: m.month,
          flower: MONTH_FLOWERS[m.month] || '',
          flowerLatin: MONTH_FLOWERS_LATIN[m.month] || ''
        })),
      createdAt: new Date().toISOString()
    };
    const res = await fetch(CFG.theatreUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      throw new Error('Theatre of Creation failed: ' + res.status);
    }
    const json = await res.json();
    return json.imageUrl || json.compositeUrl || json.url || style.img;
  }

  // THEATRE OF CREATION — watercolour-bloom anticipation animation (integrated).
  function hexA(hex, a) {
    var n = parseInt(hex.slice(1), 16);
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
  }
  function createTheatreOverlay(familyName, styleName) {
    var overlay = document.createElement('div');
    overlay.className = 'rta-theatre';
    overlay.style.cssText =
      'position:fixed; inset:0; z-index:99999; background:#f5f1e8; ' +
      'display:flex; flex-direction:column; align-items:center; justify-content:center; ' +
      'opacity:0; transition:opacity .6s ease;';
    overlay.innerHTML =
      '<canvas class="rta-theatre-canvas" style="position:absolute; inset:0; width:100%; height:100%; display:block;"></canvas>' +
      '<div style="position:relative; text-align:center; padding:2rem; max-width:540px;">' +
        '<div style="font-size:12px; letter-spacing:0.22em; text-transform:uppercase; color:#b8923a; margin-bottom:16px;">The Theatre of Creation</div>' +
        '<div class="rta-theatre-caption" style="font-family:var(--font-serif,Georgia,serif); font-size:30px; color:#2c4a1d; line-height:1.25; min-height:84px; transition:opacity .5s ease;">Gathering your flowers…</div>' +
        (familyName ? '<div style="font-size:14px; color:#5f6b52; margin-top:16px;">for ' + escapeHtml(familyName) + (styleName ? ' &middot; in the ' + escapeHtml(styleName) + ' style' : '') + '</div>' : '') +
        '<div style="margin-top:26px; display:flex; gap:6px; justify-content:center;">' +
          '<span class="rta-dot" style="width:7px;height:7px;border-radius:50%;background:#b8923a;opacity:.3;"></span>' +
          '<span class="rta-dot" style="width:7px;height:7px;border-radius:50%;background:#b8923a;opacity:.3;"></span>' +
          '<span class="rta-dot" style="width:7px;height:7px;border-radius:50%;background:#b8923a;opacity:.3;"></span>' +
        '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(function () { overlay.style.opacity = '1'; });
    var captions = ['Gathering your family\u2019s flowers\u2026','Mixing the colours of each birth month\u2026','Wetting the paper\u2026','The first washes find their place\u2026','Petals opening, one by one\u2026','Layering colour over colour\u2026','Letting the pigments bleed and bloom\u2026','Coaxing out the light\u2026','Deepening the quiet shadows\u2026','Each bloom finding its place\u2026','Balancing the composition\u2026','Warming the colours through\u2026','Adding the finest details\u2026','Stepping back to look\u2026','A few last touches\u2026','Almost ready\u2026'];
    var ci = 0, capEl = overlay.querySelector('.rta-theatre-caption');
    var capTimer = setInterval(function () {
      ci = (ci + 1) % captions.length;
      if (!capEl) return;
      capEl.style.opacity = '0';
      setTimeout(function () { capEl.textContent = captions[ci]; capEl.style.opacity = '1'; }, 500);
    }, 5200);
    var dots = overlay.querySelectorAll('.rta-dot'), di = 0;
    var dotTimer = setInterval(function () {
      dots.forEach(function (d, i) { d.style.opacity = (i === di % 3) ? '1' : '0.3'; });
      di++;
    }, 450);
    var canvas = overlay.querySelector('.rta-theatre-canvas'), ctx = canvas.getContext('2d');
    var running = true, raf = null;
    var palette = ['#e2909c', '#cf7f96', '#b9a8d6', '#9d88c6', '#a7b481', '#7d8e5a', '#d8b25a', '#e8c98f'];
    var blooms = [];
    function resize() { canvas.width = window.innerWidth; canvas.height = window.innerHeight; }
    resize();
    window.addEventListener('resize', resize);
    function spawn() {
      blooms.push({ x: Math.random() * canvas.width, y: canvas.height * 0.5 + (Math.random() - 0.5) * canvas.height * 0.8, r: 0, col: palette[Math.floor(Math.random() * palette.length)], life: 0, maxLife: 220 + Math.random() * 200, grow: 0.5 + Math.random() * 0.7 });
    }
    function frame() {
      if (!running) return;
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(245,241,232,0.07)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'multiply';
      if (blooms.length < 22 && Math.random() < 0.07) spawn();
      blooms.forEach(function (b) {
        b.life++; b.r += b.grow;
        var a = Math.sin(Math.min(b.life / b.maxLife, 1) * Math.PI) * 0.42;
        var g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        g.addColorStop(0, hexA(b.col, a));
        g.addColorStop(1, hexA(b.col, 0));
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2); ctx.fill();
      });
      blooms = blooms.filter(function (b) { return b.life < b.maxLife; });
      raf = requestAnimationFrame(frame);
    }
    for (var i = 0; i < 6; i++) spawn();
    frame();
    return { overlay: overlay, stop: function () { running = false; if (raf) cancelAnimationFrame(raf); clearInterval(capTimer); clearInterval(dotTimer); window.removeEventListener('resize', resize); } };
  }
  function removeTheatreOverlay(t) {
    if (!t) return;
    t.stop();
    t.overlay.style.opacity = '0';
    document.documentElement.style.overflow = '';
    setTimeout(function () { if (t.overlay && t.overlay.parentNode) t.overlay.parentNode.removeChild(t.overlay); }, 700);
  }

  // ============================================================
  // NAV
  // ============================================================

  function updateNav() {
    const back = byId('backBtn');
    const cont = byId('continueBtn');
    const status = byId('navStatus');
    if (!back || !cont || !status) return;

    back.disabled = state.step === 1;

    if (state.step === 1) {
      cont.disabled = !state.style;
      status.textContent = state.style
        ? 'Style chosen — continue to add your family.'
        : 'Choose a style to continue.';
    } else if (state.step === 2) {
      const valid = state.members.length > 0 &&
                    state.members.every(m => m.name.trim() && m.month);
      cont.disabled = !valid;
      status.textContent = valid
        ? state.members.length + ' ' + (state.members.length === 1 ? 'person' : 'people') + ' added.'
        : 'Add at least one person — name and birth month.';
    } else if (state.step === 3) {
      cont.disabled = state.familyName.trim().length === 0;
      status.textContent = state.familyName.trim()
        ? 'Looking good — let\'s review.'
        : 'Give it a name to continue.';
    } else if (state.step === 4) {
      cont.disabled = true;
      status.textContent = 'Ready when you are.';
      cont.style.display = 'none';
    } else {
      cont.disabled = true;
      cont.style.display = 'none';
      back.style.display = 'none';
      status.textContent = '';
    }

    if (state.step < 4) {
      cont.style.display = '';
      back.style.display = '';
    }
  }

  const backBtn = byId('backBtn');
  const contBtn = byId('continueBtn');
  if (backBtn) backBtn.addEventListener('click', () => {
    if (state.step > 1) goToStep(state.step - 1);
  });
  if (contBtn) contBtn.addEventListener('click', () => {
    if (state.step < 4) goToStep(state.step + 1);
  });

  function goToStep(n) {
    state.step = n;
    $$('.step').forEach(s => {
      s.classList.toggle('is-active', +s.dataset.step === n);
    });
    renderStepper();
    updatePreview();
    if (n === 4) refreshPricing();
    updateNav();
    // Scroll to the wizard, not the page top — the product-main section sits
    // above us, and scrolling to window.top sends the user to the wrong place.
    const wizard = document.querySelector('.rta-config-v2');
    if (wizard) {
      wizard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  const addMemberBtn = byId('addMemberBtn');
  if (addMemberBtn) addMemberBtn.addEventListener('click', () => {
    if (state.members.length >= MAX_MEMBERS) return;
    state.members.push({ name: '', month: '' });
    renderMembers();
    setTimeout(() => {
      const inputs = root.querySelectorAll('input[data-field="name"]');
      if (inputs.length) inputs[inputs.length - 1].focus();
    }, 50);
    updatePreview();
    updateNav();
  });

  // ============================================================
  // HELPERS
  // ============================================================

  function escapeHtml(s) {
    return (s || '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }

  function persistFamily() {
    try { sessionStorage.setItem('rta_family', JSON.stringify({ familyName: state.familyName, members: state.members })); } catch (e) {}
  }

  function titleCaseName(s) {
    return (s || '').toString().replace(/(^|[\s'\-])([a-z\u00E0-\u00FF])/g, function (_, p, c) { return p + c.toUpperCase(); });
  }
  function lowResUrl(url) {
    if (!url) return url;
    return url.indexOf('/upload/') !== -1 ? url.replace('/upload/', '/upload/w_800,c_limit,q_auto,f_auto/') : url;
  }

  // ============================================================
  // INIT
  // ============================================================

  try {
    var __saved = JSON.parse(sessionStorage.getItem('rta_family') || 'null');
    if (__saved && Array.isArray(__saved.members) && __saved.members.length && (!state.members || !state.members.length)) {
      state.members = __saved.members;
      if (__saved.familyName) state.familyName = __saved.familyName;
      var __fi = byId('familyName'); if (__fi && state.familyName) __fi.value = state.familyName;
    }
  } catch (e) {}

  renderStepper();
  renderStyleGrid();
  renderMembers();
  updatePreview();
  refreshPricing();
  updateNav();
})();
